// services/legalResearchService.js
// Legal Research Service — Save research notes, bookmarks, search history
// Place this file in: src/services/legalResearchService.js

import { db } from '../firebase-config';
import { 
  collection, addDoc, getDocs, updateDoc, deleteDoc, doc, 
  query, where, serverTimestamp 
} from 'firebase/firestore';

// ============================================================================
// RESEARCH NOTES
// ============================================================================

export const saveResearchNote = async (userId, noteData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'researchNotes'), {
      ...noteData,
      userId,
      firmId: firmId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getResearchNotes = async (userId) => {
  try {
    const q = query(
      collection(db, 'researchNotes'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    let notes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    notes.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });
    return { success: true, notes };
  } catch (error) {
    return { success: false, error: error.message, notes: [] };
  }
};

export const updateResearchNote = async (noteId, updates) => {
  try {
    await updateDoc(doc(db, 'researchNotes', noteId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteResearchNote = async (noteId) => {
  try {
    await deleteDoc(doc(db, 'researchNotes', noteId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// BOOKMARKED RESOURCES
// ============================================================================

export const saveBookmark = async (userId, bookmarkData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'researchBookmarks'), {
      ...bookmarkData,
      userId,
      firmId: firmId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getBookmarks = async (userId) => {
  try {
    const q = query(
      collection(db, 'researchBookmarks'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    let bookmarks = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    bookmarks.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });
    return { success: true, bookmarks };
  } catch (error) {
    return { success: false, error: error.message, bookmarks: [] };
  }
};

export const deleteBookmark = async (bookmarkId) => {
  try {
    await deleteDoc(doc(db, 'researchBookmarks', bookmarkId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// SEARCH HISTORY
// ============================================================================

// jurisdictionCode: the jurisdiction the query actually ran against (Batch 1B).
// Callers pass it explicitly; it is recorded verbatim so history stays
// attributable once more than one jurisdiction exists.
export const saveSearchHistory = async (userId, firmId, queryText, jurisdictionCode = null) => {
  try {
    await addDoc(collection(db, 'researchHistory'), {
      userId,
      firmId: firmId || '',
      query: queryText,
      jurisdictionCode: jurisdictionCode || null,
      searchedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getSearchHistory = async (userId) => {
  try {
    const q = query(
      collection(db, 'researchHistory'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    let items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    items.sort((a, b) => {
      const da = a.searchedAt?.toDate?.() || new Date(0);
      const db2 = b.searchedAt?.toDate?.() || new Date(0);
      return db2 - da;
    });
    // Deduplicate: keep only the most recent entry per unique query text
    const seen = new Set();
    items = items.filter(item => {
      const key = item.query?.toLowerCase().trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return { success: true, items };
  } catch (error) {
    return { success: false, error: error.message, items: [] };
  }
};

export const deleteSearchHistoryItem = async (historyId) => {
  try {
    await deleteDoc(doc(db, 'researchHistory', historyId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// BUILT-IN LEGAL DATABASE (Ghana & West Africa)
// ============================================================================

export const LEGAL_DATABASES = [
  {
    id: 'ghana_supreme',
    name: 'Supreme Court of Ghana',
    category: 'Case Law',
    jurisdiction: 'Ghana',
    icon: '🏛️',
    color: '#ef4444',
    url: 'https://judicial.gov.gh',
    description: 'Complete database of Supreme Court decisions from independence to present.',
    searchable: true
  },
  {
    id: 'ghana_high',
    name: 'High Court of Ghana',
    category: 'Case Law',
    jurisdiction: 'Ghana',
    icon: '⚖️',
    color: '#f59e0b',
    url: 'https://judicial.gov.gh',
    description: 'High Court rulings across all divisions — Commercial, Human Rights, Land, Financial.',
    searchable: true
  },
  {
    id: 'ghana_appeal',
    name: 'Court of Appeal',
    category: 'Case Law',
    jurisdiction: 'Ghana',
    icon: '📜',
    color: '#8b5cf6',
    url: 'https://judicial.gov.gh',
    description: 'Appellate decisions reviewing High Court and Circuit Court judgments.',
    searchable: true
  },
  {
    id: 'ghana_legislation',
    name: 'Ghana Legislation',
    category: 'Legislation',
    jurisdiction: 'Ghana',
    icon: '📖',
    color: '#3b82f6',
    url: 'https://laws.ghanalegal.com',
    description: 'Constitution, Acts of Parliament, Legislative Instruments, Executive Instruments.',
    searchable: true
  },
  {
    id: 'ghana_gazette',
    name: 'Ghana Gazette',
    category: 'Legislation',
    jurisdiction: 'Ghana',
    icon: '📰',
    color: '#06b6d4',
    url: 'https://ghanalegal.com',
    description: 'Official government gazette — statutory instruments, proclamations, notices.',
    searchable: true
  },
  {
    id: 'ecowas',
    name: 'ECOWAS Court of Justice',
    category: 'Regional',
    jurisdiction: 'West Africa',
    icon: '🌍',
    color: '#10b981',
    url: 'https://courtecowas.org',
    description: 'ECOWAS Community Court decisions on human rights, treaty interpretation.',
    searchable: true
  },
  {
    id: 'african_union',
    name: 'African Court on Human Rights',
    category: 'Regional',
    jurisdiction: 'Africa',
    icon: '🏳️',
    color: '#059669',
    url: 'https://african-court.org',
    description: 'African Union human and peoples\' rights court decisions.',
    searchable: true
  },
  {
    id: 'ghana_bar',
    name: 'Ghana Bar Association Resources',
    category: 'Professional',
    jurisdiction: 'Ghana',
    icon: '👨‍⚖️',
    color: '#d97706',
    url: 'https://ghbar.org',
    description: 'Practice directions, legal opinions, professional standards.',
    searchable: false
  },
  {
    id: 'constitution',
    name: '1992 Constitution of Ghana',
    category: 'Constitution',
    jurisdiction: 'Ghana',
    icon: '🇬🇭',
    color: '#dc2626',
    url: 'https://laws.ghanalegal.com/constitution',
    description: 'The Fourth Republican Constitution with amendments and interpretive notes.',
    searchable: true
  },
  {
    id: 'african_lii',
    name: 'AfricanLII',
    category: 'Research',
    jurisdiction: 'Africa',
    icon: '📚',
    color: '#667eea',
    url: 'https://africanlii.org',
    description: 'Free access to African legal information — case law, legislation, journals.',
    searchable: true
  },
  {
    id: 'ghana_lii',
    name: 'GhanaLII',
    category: 'Research',
    jurisdiction: 'Ghana',
    icon: '🔍',
    color: '#764ba2',
    url: 'https://ghalii.org',
    description: 'Ghana Legal Information Institute — primary legal materials, free access.',
    searchable: true
  },
  {
    id: 'laws_africa',
    name: 'Laws.Africa',
    category: 'Legislation',
    jurisdiction: 'Africa',
    icon: '🌐',
    color: '#0891b2',
    url: 'https://laws.africa',
    description: 'Pan-African legislation database with machine-readable legal texts.',
    searchable: true
  }
];

export const LEGAL_CATEGORIES = [
  { id: 'all', label: 'All Resources' },
  { id: 'Case Law', label: 'Case Law' },
  { id: 'Legislation', label: 'Legislation' },
  { id: 'Constitution', label: 'Constitution' },
  { id: 'Regional', label: 'Regional Courts' },
  { id: 'Research', label: 'Research Tools' },
  { id: 'Professional', label: 'Professional' },
];

export const JURISDICTIONS = ['All', 'Ghana', 'West Africa', 'Africa'];

// ============================================================================
// SAMPLE CASE LAW (for search demo)
// ============================================================================

export const SAMPLE_CASES = [
  { id: 1, name: 'Republic v. Tommy Thompson Books Ltd', citation: '[1996-97] SCGLR 484', court: 'Supreme Court', year: 1996, area: 'Constitutional Law', summary: 'Freedom of speech and expression under Article 21(1)(a) of the 1992 Constitution.' },
  { id: 2, name: 'Tuffour v Attorney-General', citation: '[1980] GLR 637', court: 'Supreme Court', year: 1980, area: 'Constitutional Law', summary: 'The doctrine of separation of powers and judicial independence in Ghana.' },
  { id: 3, name: 'New Patriotic Party v Inspector General of Police', citation: '[1993-94] 2 GLR 459', court: 'Supreme Court', year: 1993, area: 'Constitutional Law', summary: 'Right to demonstrate and the limitations under the Public Order Act.' },
  { id: 4, name: 'Abubakar v Qualifi Engineering Co. Ltd', citation: '[2005-2006] SCGLR 413', court: 'Supreme Court', year: 2005, area: 'Commercial Law', summary: 'Principles of contract interpretation and breach of commercial agreements.' },
  { id: 5, name: 'Ghana Lotto Operators Assoc v National Lottery Authority', citation: '[2007-2008] SCGLR 1088', court: 'Supreme Court', year: 2007, area: 'Administrative Law', summary: 'Judicial review of administrative decisions and the principle of fairness.' },
  { id: 6, name: 'Bimpong-Buta v General Legal Council', citation: '[2003-2004] SCGLR 1', court: 'Supreme Court', year: 2003, area: 'Legal Profession', summary: 'Disciplinary proceedings against legal practitioners and due process.' },
  { id: 7, name: 'Asare v Attorney-General', citation: '[2003-2004] 2 SCGLR 823', court: 'Supreme Court', year: 2003, area: 'Constitutional Law', summary: 'Presidential power and executive authority under the 1992 Constitution.' },
  { id: 8, name: 'Republic v High Court (Fast Track Division), Accra; Ex Parte CHRAJ', citation: '[2007-2008] SCGLR 213', court: 'Supreme Court', year: 2007, area: 'Human Rights', summary: 'CHRAJ powers of investigation and prosecution of public officials.' },
  { id: 9, name: 'Kpebu v Attorney-General', citation: '[2018] GHASC 41', court: 'Supreme Court', year: 2018, area: 'Criminal Law', summary: 'Constitutionality of criminal libel laws and press freedom in Ghana.' },
  { id: 10, name: 'Abu Ramadan v Electoral Commission', citation: '[2020] GHASC 11', court: 'Supreme Court', year: 2020, area: 'Electoral Law', summary: 'Right to be included on the voter register and use of national ID card.' },
  { id: 11, name: 'Osei-Boateng v National Media Commission', citation: '[2012] 2 SCGLR 1038', court: 'Supreme Court', year: 2012, area: 'Media Law', summary: 'Press freedom, defamation, and the role of the National Media Commission.' },
  { id: 12, name: 'Mensah v Attorney-General', citation: '[1996-97] SCGLR 676', court: 'Supreme Court', year: 1996, area: 'Property Law', summary: 'Land rights, compulsory acquisition by the state, and fair compensation.' },
  { id: 13, name: 'In re Presidential Election Petition', citation: '[2013] SCGLR (Special Edition)', court: 'Supreme Court', year: 2013, area: 'Electoral Law', summary: 'Landmark presidential election petition challenging the 2012 election results.' },
  { id: 14, name: 'Nii Kpabi v Attorney-General', citation: '[2009] SCGLR 420', court: 'Supreme Court', year: 2009, area: 'Chieftaincy Law', summary: 'Chieftaincy disputes and the jurisdiction of traditional authorities.' },
  { id: 15, name: 'Edusei v Attorney-General', citation: '[1998-99] SCGLR 753', court: 'Supreme Court', year: 1998, area: 'Criminal Law', summary: 'Right to fair trial, presumption of innocence, and burden of proof.' },
];
